import { periodenname, t } from './sprache'

/**
 * Der Zeitraum der Kontextzeile (7.3 Abschnitt 2g).
 *
 * **Ein Zeitschieber von–bis, in Rastereinheiten.** Das Raster — Monat,
 * Quartal, Jahr — entscheidet, in welchen Zeiteinheiten die Zeit gezählt wird:
 * Was der Anwender wählt, sind Zeiteinheiten, und was der Bericht zeigt, sind
 * genau diese Zeiteinheiten. Bilanz und Spiegel nehmen das rechte Ende als
 * Stichtag, die Kapitalflussrechnung rechnet über von–bis, das Periodenbild
 * stellt die Zeiteinheiten dazwischen nebeneinander.
 *
 * Gerechnet wird durchgehend in **Monaten** (`jjjjmm`); die Zeiteinheit ist eine
 * Sicht darauf. So bleibt die Periode, die überall sonst gilt, ein Monat —
 * und der Wechsel des Rasters ist eine Rundung, keine Umrechnung.
 */

export type Raster = 'monat' | 'quartal' | 'jahr'

/** Eine Zeiteinheit des Schiebers: ein Monat, ein Quartal, ein Jahr — als Monatsspanne. */
export type Zeiteinheit = {
  schluessel: string
  von: number
  bis: number
  /** Ob in dieser Zeiteinheit mindestens ein Monat einen Lauf hat. */
  hatLauf: boolean
}

export function jahrVon(periode: number): number {
  return Math.floor(periode / 100)
}

export function monatVon(periode: number): number {
  return periode % 100
}

export function periodeAus(jahr: number, monat: number): number {
  return jahr * 100 + monat
}

/* --------------------------------------------------------------------------
   Die Schreibweise einer Periode am Bildschirm: MM.JJJJ (R502)
   --------------------------------------------------------------------------

   **Gerechnet und gespeichert wird weiter in `jjjjmm`** — eine Zahl, die sich
   sortieren lässt, ist die richtige Speicherform, und jede Tabelle, jeder
   Schlüssel und jeder Prüfcode hängt daran. **Geschrieben wird MM.JJJJ**, weil
   ein Fachanwender ein Datum so liest: Tag vor Monat vor Jahr, und wo der Tag
   fehlt, bleibt Monat vor Jahr.

   `202609` in ein Eingabefeld zu schreiben verlangt vom Anwender, in der
   Speicherform zu denken. Das ist die Stelle, an der eine Software ihre
   Innereien nach außen kehrt.

   **Seit dem 2026-09-30 gilt das für die Eingabe** (R839 — Eine Periode liest
   sich als Monat und Jahr; 7.2 Abschnitt 21): Gelesen wird *Dezember 2026*,
   MM.JJJJ ist die Feldform — das, was im Periodenfeld steht, nachdem der
   Anwender getippt hat. In einer Zelle oder einem Satz steht sie nicht. */

/**
 * Eine Periode in der Feldform — `202609` → `09.2026`.
 *
 * **Für das Eingabefeld und nur dafür** (R839): Was gelesen wird, schreibt
 * `periodenname` aus.
 */
export function alsPeriodenfeld(periode: number | null | undefined): string {
  if (periode === null || periode === undefined) {
    return ''
  }

  const jahr = jahrVon(periode)
  const monat = monatVon(periode)

  // Was keine Periode ist, wird nicht zu einer gemacht: Eine Zahl, die
  // irgendwo als Periode gemeint war und keine ist, soll als das dastehen, was
  // sie ist — sonst sucht jemand einen Fehler in der Anzeige statt im Wert.
  if (jahr <= 0 || monat < 1 || monat > 12) {
    return String(periode)
  }

  return `${String(monat).padStart(2, '0')}.${jahr}`
}

/**
 * Was der Anwender getippt hat, als Periode — `null`, wenn es keine ist.
 *
 * **Großzügig bei der Schreibweise** (R839 — Eine Periode liest sich als Monat
 * und Jahr; Nachtrag an R502 vom 2026-09-30). Angenommen werden:
 *
 * - Monat vor Jahr mit Trennzeichen — `12.2026`, `12/2026`, `12-2026`; Punkt,
 *   Schrägstrich und Bindestrich sind dasselbe Zeichen für denselben Zweck,
 *   und wer `9.2026` tippt, meint den September.
 * - Jahr vor Monat mit Trennzeichen — `2026-12`, ebenso `2026.12` und
 *   `2026/12`.
 * - sechs Ziffern ohne Trennzeichen — die Speicherform `202612` und
 *   Monat-Jahr `122026`.
 *
 * **Berichtigt am 2026-09-30:** Bis dahin stand hier, die Speicherform werde
 * nicht angenommen — sie sei von `09.2026` nicht zu unterscheiden, sobald
 * jemand den Punkt vergisst. Der Auftraggeber hat die Eingabe geöffnet
 * (7.2 Abschnitt 21, Entscheidung 2), und die Falle gibt es bei Jahren von
 * 1900 bis 2999 nicht: **Sechs Ziffern sind nur in einer Lesart eine
 * Periode.** Als Jahr-Monat beginnen sie mit 19 bis 29, als Monat-Jahr mit
 * 01 bis 12 — keine Ziffernfolge ist beides. `202609` ist nur als Jahr-Monat
 * der September 2026 (einen Monat 20 gibt es nicht), `092026` nur als
 * Monat-Jahr (ein Jahr 920 liegt außerhalb).
 *
 * **Eine Wahrheit, zwei Sprachen:** Der Server liest dieselben Schreibweisen
 * in `Periode.TryLies` (`src/Coko.Domain/Periode.cs`). Wer hier eine
 * Schreibweise ändert, ändert sie dort — und im Hinweis des Feldes
 * (`periode.muster.hinweis`).
 */
export function ausPeriodenfeld(text: string): number | null {
  const eingabe = text.trim()

  const monatVorn = /^(\d{1,2})\s*[./-]\s*(\d{4})$/.exec(eingabe)
  if (monatVorn) {
    return periodeOderKeine(Number(monatVorn[2]), Number(monatVorn[1]))
  }

  const jahrVorn = /^(\d{4})\s*[./-]\s*(\d{1,2})$/.exec(eingabe)
  if (jahrVorn) {
    return periodeOderKeine(Number(jahrVorn[1]), Number(jahrVorn[2]))
  }

  if (/^\d{6}$/.test(eingabe)) {
    // Erst die Speicherform, dann Monat-Jahr. Die Reihenfolge entscheidet
    // nichts: Höchstens eine der beiden Lesarten ergibt eine Periode.
    return (
      periodeOderKeine(Number(eingabe.slice(0, 4)), Number(eingabe.slice(4))) ??
      periodeOderKeine(Number(eingabe.slice(2)), Number(eingabe.slice(0, 2)))
    )
  }

  return null
}

/** Jahr 1900 bis 2999, Monat 1 bis 12 — sonst ist es keine Periode. */
function periodeOderKeine(jahr: number, monat: number): number | null {
  if (monat < 1 || monat > 12 || jahr < 1900 || jahr > 2999) {
    return null
  }

  return periodeAus(jahr, monat)
}

export function naechsterMonat(periode: number): number {
  const m = monatVon(periode)
  return m === 12 ? periodeAus(jahrVon(periode) + 1, 1) : periode + 1
}

/** Die Zeiteinheit des Rasters, in der ein Monat liegt. */
export function einheitVon(periode: number, raster: Raster): { von: number; bis: number } {
  const jahr = jahrVon(periode)
  const monat = monatVon(periode)

  if (raster === 'jahr') {
    return { von: periodeAus(jahr, 1), bis: periodeAus(jahr, 12) }
  }

  if (raster === 'quartal') {
    const erster = Math.floor((monat - 1) / 3) * 3 + 1
    return { von: periodeAus(jahr, erster), bis: periodeAus(jahr, erster + 2) }
  }

  return { von: periode, bis: periode }
}

/**
 * Die Zeiteinheiten des Schiebers: **lückenlos** von der Zeiteinheit des ersten bis
 * zur Zeiteinheit des letzten Laufs. Eine Zeiteinheit ohne Lauf steht mit — als
 * Lücke, die man sieht, nicht als eine, die verschwiegen wird.
 */
export function einheiten(laufperioden: readonly number[], raster: Raster): Zeiteinheit[] {
  if (laufperioden.length === 0) {
    return []
  }

  const geordnet = [...laufperioden].sort((a, b) => a - b)
  const laeufe = new Set(geordnet)
  const erste = einheitVon(geordnet[0]!, raster)
  const letzte = einheitVon(geordnet[geordnet.length - 1]!, raster)

  const liste: Zeiteinheit[] = []
  let monat = erste.von
  while (monat <= letzte.bis) {
    const e = einheitVon(monat, raster)
    let hatLauf = false
    for (let m = e.von; m <= e.bis; m = naechsterMonat(m)) {
      if (laeufe.has(m)) {
        hatLauf = true
      }
    }

    liste.push({ schluessel: einheitenschluessel(e, raster), von: e.von, bis: e.bis, hatLauf })
    monat = naechsterMonat(e.bis)
  }

  return liste
}

function einheitenschluessel(e: { von: number; bis: number }, raster: Raster): string {
  if (raster === 'jahr') {
    return String(jahrVon(e.von))
  }

  if (raster === 'quartal') {
    return `${jahrVon(e.von)}Q${Math.floor((monatVon(e.von) - 1) / 3) + 1}`
  }

  return String(e.von)
}

/** Wie eine Zeiteinheit heißt: „April 2026", „Q2 2026", „2026". */
export function einheitenname(e: { von: number; bis: number }, raster: Raster): string {
  if (raster === 'jahr') {
    return String(jahrVon(e.von))
  }

  if (raster === 'quartal') {
    return t('zeitraum.quartal', {
      quartal: String(Math.floor((monatVon(e.von) - 1) / 3) + 1),
      jahr: String(jahrVon(e.von)),
    })
  }

  return periodenname(e.von)
}

/**
 * Zusammenhängende Strecken ohne Lauf — je Strecke ein Band auf der Achse
 * (R331).
 *
 * **Ein Stück und nicht zwölf Striche:** Ein Strich je Monat war ein Muster,
 * das niemand lesen konnte; ein Band lässt sich beschriften.
 */
export function luecken(liste: readonly Zeiteinheit[]): { von: number; bis: number }[] {
  const baender: { von: number; bis: number }[] = []

  for (let i = 0; i < liste.length; i++) {
    if (liste[i]!.hatLauf) {
      continue
    }

    const von = i
    while (i + 1 < liste.length && !liste[i + 1]!.hatLauf) {
      i++
    }

    baender.push({ von, bis: i })
  }

  return baender
}

/**
 * Die Jahre der Achse mit ihrer Spanne — der Maßstab unter dem Streifen
 * (R331).
 *
 * Ohne ihn sieht man beim Ziehen nicht, wo man gerade ist, ohne den Text
 * danebenzulesen.
 */
export function jahre(liste: readonly Zeiteinheit[]): { jahr: number; von: number; bis: number }[] {
  const reihe: { jahr: number; von: number; bis: number }[] = []

  liste.forEach((e, i) => {
    const jahr = jahrVon(e.von)
    const letzter = reihe[reihe.length - 1]

    if (letzter && letzter.jahr === jahr) {
      letzter.bis = i
      return
    }

    reihe.push({ jahr, von: i, bis: i })
  })

  return reihe
}

/** An welcher Stelle des Schiebers ein Monat liegt — oder −1, wenn außerhalb. */
export function stelleVon(liste: readonly Zeiteinheit[], periode: number): number {
  return liste.findIndex((e) => e.von <= periode && periode <= e.bis)
}

/**
 * Beim Rasterwechsel werden **beide Enden nach außen gerundet**: Februar bis
 * April wird Q1 bis Q2, April allein wird Q2. Nichts, was gewählt war, fällt
 * heraus; der Zeitraum wird eher länger.
 */
export function runde(von: number, bis: number, raster: Raster): { von: number; bis: number } {
  return { von: einheitVon(von, raster).von, bis: einheitVon(bis, raster).bis }
}

/**
 * Der Stichtag einer Zeiteinheit: der **letzte Monat mit Lauf** in ihr. Wer Q2
 * wählt, sieht in der Bilanz den Juni — und, solange erst der April gerechnet
 * ist, den April, benannt nach dem, was wirklich darin steckt (R185).
 */
export function stichtag(e: { von: number; bis: number }, laufperioden: readonly number[]): number {
  let letzter = 0
  for (const p of laufperioden) {
    if (p >= e.von && p <= e.bis && p > letzter) {
      letzter = p
    }
  }

  return letzter === 0 ? e.bis : letzter
}

/**
 * Die Vorbelegung beim Öffnen eines Kreises: **das Geschäftsjahr bis zum
 * jüngsten Lauf** — vom Januar seines Jahres, nicht früher als der erste
 * Lauf, bis zu ihm selbst.
 */
export function vorbelegung(laufperioden: readonly number[]): { von: number; bis: number } | null {
  if (laufperioden.length === 0) {
    return null
  }

  const geordnet = [...laufperioden].sort((a, b) => a - b)
  const bis = geordnet[geordnet.length - 1]!
  const januar = periodeAus(jahrVon(bis), 1)
  return { von: Math.max(geordnet[0]!, januar), bis }
}

/**
 * Welches Ende beim Aufsetzen festgehalten wird — der **Anker** des Zugs
 * (R335).
 *
 * **Wer ein Ende anfasst, zieht dieses Ende; das andere steht.** Vorher zog
 * jedes Aufsetzen die Auswahl auf die getroffene Zeiteinheit zusammen — wer den
 * linken Griff anfasste, sah den rechten mitspringen (Befund vom 2026-09-11).
 *
 *  - Auf dem **linken Ende** — seiner Zeiteinheit oder seinem Griff — ist der
 *    Anker das rechte Ende; auf dem rechten Ende umgekehrt. Sind beide in
 *    Reichweite (eine schmale Auswahl), zählt das nähere.
 *  - **Sonst** ist der Anker die getroffene Zeiteinheit: Klicken wählt sie,
 *    Ziehen spannt von ihr aus auf (R331).
 *  - **Mit Umschalt** wird das entferntere Ende festgehalten, damit sich die
 *    Auswahl erweitert und nicht von der falschen Seite zusammenzieht.
 *
 * **Ein angefasstes Ende überholt das andere nicht.** Das gezogene Ende
 * läuft bis zum Anker und nicht darüber hinaus; sonst zöge, wer das linke
 * Ende zu weit nach rechts schiebt, plötzlich das rechte mit — der zweite
 * Befund vom 2026-09-11. Deshalb sagt die Antwort auch, **welches** Ende
 * angefasst ist: `ende` ist `'links'`, `'rechts'` oder `null`, wenn von der
 * Zeiteinheit aus aufgespannt wird (dann darf der Zug in beide Richtungen).
 *
 * `lage` ist der Zeiger in Zeiteinheiten ab Achsenanfang, mit Nachkommastellen;
 * `stelle` die getroffene Zeiteinheit, auf die Achse begrenzt. `griffweite` ist
 * die halbe Breite des Griffs in Zeiteinheiten: Der Griff steht auf der Fuge und
 * ragt in die Nachbareinheit hinein — wer ihn dort anfasst, meint ihn.
 */
export function ankerFuer(
  lage: number,
  stelle: number,
  links: number,
  rechts: number,
  umschalt: boolean,
  griffweite: number,
): { anker: number; ende: Griffende } {
  if (umschalt) {
    const anker = Math.abs(stelle - links) <= Math.abs(stelle - rechts) ? rechts : links
    return { anker, ende: null }
  }

  // Ein Zeitraum aus einer Zeiteinheit hat beide Enden auf ihr: Wer sie trifft,
  // fasst kein Ende an, sondern spannt von ihr aus auf — in beide Richtungen.
  if (links === rechts && stelle === links) {
    return { anker: stelle, ende: null }
  }

  const zumLinken = Math.abs(lage - links)
  const zumRechten = Math.abs(lage - (rechts + 1))
  const amLinken = stelle === links || zumLinken <= griffweite
  const amRechten = stelle === rechts || zumRechten <= griffweite

  if (amLinken && amRechten) {
    return zumLinken <= zumRechten ? { anker: rechts, ende: 'links' } : { anker: links, ende: 'rechts' }
  }

  if (amLinken) {
    return { anker: rechts, ende: 'links' }
  }

  if (amRechten) {
    return { anker: links, ende: 'rechts' }
  }

  return { anker: stelle, ende: null }
}

/** Welches Ende ein Zug verschiebt — oder keines, wenn er von einer Zeiteinheit aus aufspannt. */
export type Griffende = 'links' | 'rechts' | null

/**
 * Die Ränder eines Zugs aus Anker und Zeiger.
 *
 * Ein angefasstes Ende bleibt auf seiner Seite des Ankers: Wer das linke Ende
 * über das rechte hinauszieht, steht am rechten — der Zeitraum ist dann eine
 * Zeiteinheit lang, und das rechte Ende hat sich nicht bewegt. Ohne angefasstes
 * Ende liegt der Zeitraum zwischen Anker und Zeiger, in beide Richtungen.
 */
export function raender(anker: number, stelle: number, ende: Griffende): { links: number; rechts: number } {
  if (ende === 'links') {
    return { links: Math.min(anker, stelle), rechts: anker }
  }

  if (ende === 'rechts') {
    return { links: anker, rechts: Math.max(anker, stelle) }
  }

  return { links: Math.min(anker, stelle), rechts: Math.max(anker, stelle) }
}

/**
 * Wie breit ein Griff greift, in Pixeln je Seite: der Kreis auf der Fuge
 * (11 px) und ein wenig Luft.
 */
export const GRIFFWEITE = 7

/**
 * Wie viele Zeiteinheiten der Streifen höchstens zeigt (R333).
 *
 * **Vier Jahre in Monaten.** Darüber wird eine Zeiteinheit schmaler als zehn
 * Pixel, und damit fängt genau das Problem wieder an, das der Zeitstreifen
 * gelöst hat: Man trifft sie nicht mehr. Im Quartalsraster sind dieselben 48
 * Zeiteinheiten zwölf Jahre, im Jahresraster achtundvierzig — dort reicht es
 * praktisch immer.
 */
export const FENSTER = 48

/**
 * Der Ausschnitt, den der Streifen zeigt: die erste sichtbare Zeiteinheit (R333).
 *
 * **Der Ausschnitt folgt der Auswahl, nicht umgekehrt.** Wer mit der Tastatur
 * an den Rand läuft oder im Überblick springt, soll seine Auswahl weiter
 * sehen; also wandert das Fenster so wenig wie nötig, bis beide Enden darin
 * liegen. Passt die ganze Achse hinein, gibt es keinen Ausschnitt.
 */
export function ausschnitt(
  anzahl: number,
  links: number,
  rechts: number,
  gemerkt: number,
  groesse: number = FENSTER,
): number {
  if (anzahl <= groesse) {
    return 0
  }

  const hoechstens = anzahl - groesse
  let von = Math.max(0, Math.min(hoechstens, gemerkt))

  if (links < von) {
    von = links
  }

  if (rechts > von + groesse - 1) {
    von = rechts - groesse + 1
  }

  return Math.max(0, Math.min(hoechstens, von))
}
